/* Sprig & Scout — fade-in on scroll.
   Self-contained: does not depend on the theme's animations.js running first.

   Two rules, in this order:
     1. Nothing the visitor can see is ever left invisible.
     2. Anything below the fold fades in as it arrives.
   The guarantee is enforced by a viewport sweep, NOT by revealing everything on
   a timer — a blanket reveal satisfies rule 1 by destroying rule 2. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) return;

  /* Deliberately NOT .product-card: collection-grid.js rearranges those nodes
     after load (equal-height + swiper), so a card tagged here can be replaced by
     a copy the observer was never given. The section fades as a unit instead,
     which still fades the tiles in. */
  var TARGETS = [
    { sel: '#MainContent > .shopify-section > *', stagger: false },
    { sel: '.ss-stages__row', stagger: true },
    { sel: '.ss-gtee__seal, .ss-gtee__text', stagger: true }
  ];

  var io = null;
  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };
  var qsa = function (sel) { return document.querySelectorAll(sel); };

  function show(el) {
    el.classList.add('ss-in');
    el.removeAttribute('data-ss-obs');
  }

  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        show(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    return io;
  }

  /* Already on screen at first paint? Show it immediately — a hero that fades
     in after the page has settled reads as a glitch, not as motion. */
  function inView(el, margin) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight * (margin || 1);
  }

  function queue(el) {
    if (inView(el)) { show(el); return; }
    el.setAttribute('data-ss-obs', '');
    observer().observe(el);
  }

  /* Tag anything new. Idempotent — safe to call on every section load. */
  function scan() {
    var found = 0;
    TARGETS.forEach(function (t) {
      each(qsa(t.sel), function (el, i) {
        if (el.hasAttribute('data-ss-reveal')) return;
        el.setAttribute('data-ss-reveal', '');
        if (t.stagger) el.style.setProperty('--ss-order', String(i % 6));
        found++;
        queue(el);
      });
    });
    if (found) document.documentElement.classList.add('ss-reveal-ready');
  }

  /* The safety net. Anything still hidden that has scrolled into view gets
     shown, whatever went wrong — a swapped node, an observer that never fired.
     0.92 mirrors the observer's -8% margin so this never pre-empts a fade. */
  function sweep() {
    scan();
    var hidden = qsa('[data-ss-reveal]:not(.ss-in)');
    each(hidden, function (el) {
      if (inView(el, 0.92)) show(el);
      else if (!el.hasAttribute('data-ss-obs')) queue(el);   // orphaned clone
    });
    return hidden.length;
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      if (sweep() === 0) window.removeEventListener('scroll', onScroll);
    });
  }

  function init() {
    scan();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Late arrivals: apps, lazy images resizing the page, the theme's own scripts. */
  window.addEventListener('load', function () {
    setTimeout(sweep, 400);
    setTimeout(sweep, 1500);
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () { scan(); sweep(); });
  }
})();

/* Sprig & Scout — "jump to buy" for the story-mode PDP.
   The buy section is the theme's own and must not be modified, so there is no
   anchor element inside it to link to. In a JSON template its wrapper id is
   shopify-section-template--<template id>__<section id>, and Shopify assigns the
   template id, so it cannot be written into a link at author time. Match the
   stable half of the id instead.
   Separate IIFE: the reveal code above returns early under reduced motion, and
   this must keep working regardless. */
(function () {
  'use strict';

  var LINKS  = 'a[href="#buy"], a[href$="#buy"]';
  var TARGET = '[id*="__shop_product_details"]';
  var GAP    = 12;

  /* Only sticky/fixed chrome actually covers the target; a static header does not. */
  function chromeHeight() {
    var h = 0;
    Array.prototype.forEach.call(
      document.querySelectorAll('header, [id*="__header"], .header-wrapper, .announcement-bar'),
      function (el) {
        var pos = window.getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'sticky') h = Math.max(h, el.offsetHeight);
      }
    );
    return h;
  }

  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest && e.target.closest(LINKS);
    if (!link) return;

    var el = document.querySelector(TARGET);
    if (!el) return;   /* no buy section here — leave the link to the browser */

    e.preventDefault();

    var top = el.getBoundingClientRect().top + window.pageYOffset - chromeHeight() - GAP;
    top = Math.max(0, top);

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && 'scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ top: top, behavior: 'smooth' });
    } else {
      window.scrollTo(0, top);   /* options-object form is ignored by old engines */
    }

    /* Keyboard and screen-reader users should land in the buy block, not stay
       at the hero with only the viewport moved. */
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    if (el.focus) el.focus({ preventScroll: true });
  });
})();

/* Sprig & Scout — "back to where you came from".
   The size guide is reached from the size-guide drawer on the PDP, so a visitor
   who follows it lands on a page with no route back to the product they were
   half-way through choosing.

   Progressive enhancement, deliberately. The markup ships a real <a> with a real
   href, so the control still works with JS off and modified clicks (new tab,
   new window) behave like links. The script upgrades a plain click to
   history.back(), which is the one thing an href cannot do: it restores the
   previous page's scroll position, returning the visitor to the size picker
   rather than dropping them at the top of the product page.

   Separate IIFE for the same reason as the block above: the reveal code returns
   early under reduced motion, and this must work regardless. */
(function () {
  'use strict';

  var SEL = '.ss-back';

  function sameOrigin(url) {
    if (!url) return false;
    var a = document.createElement('a');
    a.href = url;                     /* resolves relative and protocol-relative urls */
    return a.protocol === window.location.protocol && a.host === window.location.host;
  }

  var from = document.referrer;
  /* Only treat it as "back" if the previous page was ours. Arriving cold — from
     search, a shared link, a new tab — leaves the authored fallback href alone. */
  var internal = sameOrigin(from) && from !== window.location.href;

  if (internal) {
    Array.prototype.forEach.call(document.querySelectorAll(SEL), function (el) {
      el.setAttribute('href', from);
    });
  }

  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest && e.target.closest(SEL);
    if (!link) return;

    /* Leave modified clicks to the browser so "open in new tab" still works. */
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    /* No usable history: let the href navigate instead of stranding the visitor. */
    if (!internal || window.history.length <= 1) return;

    e.preventDefault();
    window.history.back();
  });
})();

/* Sprig & Scout — silence looping video for visitors who ask for less motion.
   The tab videos are deliberately autoplay + loop + muted, which is GIF behaviour,
   and an endlessly looping clip is precisely what prefers-reduced-motion exists to
   stop. Markup keeps the attributes so everyone else gets the motion for free;
   this pauses them for the people who opted out and hands them controls, so the
   content is still reachable rather than simply frozen.

   A capture-phase 'play' listener rather than a single sweep at load: the browser
   may start playback after our sweep — a lazily-decoded video, or one inside a tab
   panel that was hidden at load and only revealed on click. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduce) return;

  function calm(v) {
    if (!v || v.tagName !== 'VIDEO') return;
    if (!v.hasAttribute('autoplay') && !v.hasAttribute('loop')) return;
    v.autoplay = false;
    v.loop = false;
    if (!v.paused) v.pause();
    if (!v.hasAttribute('controls')) v.setAttribute('controls', '');
  }

  document.addEventListener('play', function (e) { calm(e.target); }, true);

  function sweep() {
    Array.prototype.forEach.call(document.querySelectorAll('video[autoplay], video[loop]'), calm);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();

/* Sprig & Scout — only the visible looping video plays.
   The tab panels all exist in the DOM at once, so a page with video in two tabs
   has two autoplaying clips while the visitor can see one. Hidden panels are
   display:none and mostly will not start, but "mostly" is not a guarantee and a
   clip that does start costs bandwidth and battery for something nobody is
   looking at — on a mobile-first PDP that matters.

   An IntersectionObserver rather than hooking the theme's tab buttons: it needs
   to know nothing about the tab markup, so it keeps working if that section is
   restyled, and it covers the ordinary case of a video scrolled off screen too.

   Deliberately separate from the reduced-motion block above, which returns early
   for everyone else; that block owns the reduced-motion case and pauses for good. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) return;

  function wire() {
    var vids = document.querySelectorAll('video[autoplay][loop]');
    if (!vids.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          /* play() rejects if the browser declines autoplay; that is not an error
             worth surfacing, and an unhandled rejection would show in the console. */
          if (v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.1 });

    Array.prototype.forEach.call(vids, function (v) { io.observe(v); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();

/* ---------------------------------------------------------------------------
   Product title: smaller on mobile.
   The title block writes `font-size: <n>px !important` inline on the <h1>, and
   inline !important is unbeatable from a stylesheet. The theme computes a
   data-mobile-font-size on the same element but nothing ever reads it, so we
   do: on narrow screens the title becomes 70% of its desktop size (40 → 28,
   which is the mobile size the PDP section headings already use). Follows any
   size change made in the editor because it reads the desktop value off the
   element rather than hard-coding one. */
(function () {
  var MOBILE = '(max-width: 768px)';
  var RATIO = 0.7;

  function apply() {
    var titles = document.querySelectorAll('.shop-product-title[data-font-size]');
    if (!titles.length) return;
    var mobile = window.matchMedia(MOBILE).matches;
    Array.prototype.forEach.call(titles, function (h) {
      var desktop = parseFloat(h.getAttribute('data-font-size'));
      if (!desktop) return;
      var px = mobile ? Math.round(desktop * RATIO) : desktop;
      h.style.setProperty('font-size', px + 'px', 'important');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  var mq = window.matchMedia(MOBILE);
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);
  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', apply);
})();

/* ---------------------------------------------------------------------------
   Filter gallery media by variant (vendor patch T15 supplies the data).
   With the setting on, media that is another variant's featured image is
   taken out of the gallery until that variant is selected; media not assigned
   to any variant always stays.

   Detach rather than hide: product-gallery.js captures `.shop-main-image` into
   an array once and navigates by index, so a display:none slide would still be
   a stop on the arrows and a dot on the pager. Each removed node leaves a
   comment placeholder so it returns to its original position, and the gallery
   is re-initialised afterwards so its arrays, dots and indicators match what
   is actually on screen. */
(function () {
  function config(container) {
    var el = container.querySelector('script[type="application/json"][data-ss-variant-media]');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent);
      if (!data || !data.variantMediaIds || !data.variantMediaIds.length) return null;
      return { ids: data.variantMediaIds.map(String), current: String(data.currentMediaId || '') };
    } catch (e) {
      return null;
    }
  }

  /* The slide for an image is the zoom wrapper; for video and external media
     the element carrying .shop-main-image is itself the slide. */
  function slideOf(el) {
    return el.closest('.shop-image-zoom-wrapper') || el;
  }

  function restore(container) {
    (container.__ssDetached || []).forEach(function (entry) {
      if (entry.placeholder.parentNode) {
        entry.placeholder.parentNode.insertBefore(entry.node, entry.placeholder);
        entry.placeholder.parentNode.removeChild(entry.placeholder);
      }
    });
    container.__ssDetached = [];
  }

  function reinit(container) {
    if (!window.ProductGallery) return;
    /* Drop the stale instance from the registry, or it keeps receiving
       variant events and acting on nodes that are no longer in the document. */
    if (window.ProductGallery.getAll) {
      var reg = window.ProductGallery.getAll();
      for (var i = reg.length - 1; i >= 0; i--) {
        if (reg[i].container === container) reg.splice(i, 1);
      }
    }
    container.__galleryInstance = null;
    window.ProductGallery.initAll();
  }

  function apply(container, currentId) {
    var cfg = container.__ssVariantMedia || (container.__ssVariantMedia = config(container));
    if (!cfg) return;
    if (currentId) cfg.current = String(currentId);

    restore(container);

    var nodes = container.querySelectorAll('.shop-main-image[data-media-id], .shop-thumbnail[data-media-id]');
    Array.prototype.forEach.call(nodes, function (el) {
      var id = String(el.getAttribute('data-media-id') || '');
      if (!id || cfg.ids.indexOf(id) === -1 || id === cfg.current) return;
      var slide = slideOf(el);
      if (!slide.parentNode) return;
      var placeholder = document.createComment('ss-media ' + id);
      slide.parentNode.insertBefore(placeholder, slide);
      slide.parentNode.removeChild(slide);
      container.__ssDetached.push({ node: slide, placeholder: placeholder });
    });

    /* The pre-paint style has done its job; leaving it would re-hide a slide
       the moment its variant is selected. */
    var style = container.querySelector('style[data-ss-variant-media-style]');
    if (style) style.parentNode.removeChild(style);

    reinit(container);

    var instance = container.__galleryInstance;
    if (instance && currentId && instance.updateWithVariant) {
      instance.updateWithVariant({ mediaId: currentId });
    }
  }

  function applyAll(currentId) {
    document.querySelectorAll('.product-gallery').forEach(function (container) {
      apply(container, currentId);
    });
  }

  function start() {
    applyAll(null);
    document.addEventListener('variant:imageChanged', function (e) {
      if (e.detail && e.detail.mediaId) applyAll(String(e.detail.mediaId));
    });
    document.addEventListener('variantImageSelected', function (e) {
      if (e.detail && e.detail.mediaId) applyAll(String(e.detail.mediaId));
    });
    document.addEventListener('simple-variant-picker:variantChange', function (e) {
      var v = e.detail && e.detail.variant;
      if (v && v.featured_media && v.featured_media.id) applyAll(String(v.featured_media.id));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* Buy-box FAQ (snippets/new-product-faq.liquid).
   The vendor script caps an open answer at a height measured once on click, which
   comes up short on long answers and clips the last lines. Once the open animation
   finishes, release the cap; before any click, pin released answers back to a pixel
   height so the vendor close animation still runs. Lists whose items all start with
   an emoji get no bullet, so they don't show two markers. */
(function () {
  document.addEventListener('transitionend', function (e) {
    var a = e.target;
    if (e.propertyName !== 'max-height' || !a.classList || !a.classList.contains('faq-answer')) return;
    var item = a.closest('.faq-item');
    if (item && item.classList.contains('active')) a.style.maxHeight = 'none';
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('.faq-question')) return;
    document.querySelectorAll('.faq-item.active .faq-answer').forEach(function (a) {
      if (a.style.maxHeight === 'none') {
        a.style.maxHeight = a.scrollHeight + 'px';
        a.offsetHeight; // commit the pixel height before the vendor handler sets it to 0
      }
    });
  }, true);

  function markEmojiLists() {
    var emoji = /^\p{Extended_Pictographic}/u;
    document.querySelectorAll('.faq-answer-inner ul').forEach(function (ul) {
      var items = Array.prototype.slice.call(ul.children);
      if (items.length && items.every(function (li) { return emoji.test(li.textContent.trim()); })) {
        ul.classList.add('ss-plain-list');
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markEmojiLists);
  } else {
    markEmojiLists();
  }
})();
